/**
 * NetobsAuthorityLegendContent (NO-8) — kit tool `authorityOverlay`. The color
 * legend for the 4 authority halos (LOCAL/REMOTE/BOT/NPC).
 *
 * Opening this kit tool (mounting this content) ALSO enables the scene-wide
 * halos via AuthorityOverlayState; closing it (unmount) disables them. The kit
 * only mounts content while the tool is enabled, so the mount/unmount lifecycle
 * IS the on/off — no separate React→singleton bridge effect needed (it used to
 * live in UIContext as the showNetobsAuthorityOverlay useEffect). Renderers
 * (NetobsAuthorityHalo) read isAuthorityOverlayEnabled() per-frame, unchanged.
 *
 * Migrated off the UIContext bridge into the 🛠 kit, 2026-05-30.
 */

import React, { useEffect } from 'react';
import { NETOBS_COLORS } from './colors';
import { setAuthorityOverlayEnabled } from '../../systems/netobs/AuthorityOverlayState';

interface LegendRowProps {
  color: string;
  label: string;
  detail: string;
}

const LegendRow: React.FC<LegendRowProps> = ({ color, label, detail }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, lineHeight: 1.5 }}>
    <span
      style={{
        display: 'inline-block',
        width: 14,
        height: 14,
        borderRadius: '50%',
        background: color,
        border: '1px solid rgba(255,255,255,0.15)',
        boxShadow: `0 0 6px ${color}66`,
        flexShrink: 0,
      }}
    />
    <span style={{ color, fontWeight: 600, minWidth: 56 }}>{label}</span>
    <span style={{ color: '#aaa', fontSize: 10 }}>{detail}</span>
  </div>
);

export const NetobsAuthorityLegendContent: React.FC = () => {
  // Tool open → scene halos on; tool closed (unmount) → halos off.
  useEffect(() => {
    setAuthorityOverlayEnabled(true);
    return () => setAuthorityOverlayEnabled(false);
  }, []);

  return (
    <div
      data-testid="netobs-authority-legend"
      style={{
        padding: '8px 12px',
        color: '#ddd',
        fontFamily: 'monospace',
        fontSize: 11,
      }}
    >
      <div style={{ color: '#7af', marginBottom: 6, letterSpacing: '0.05em', fontSize: 10 }}>
        AUTHORITY OVERLAY
      </div>
      <LegendRow color={NETOBS_COLORS.AUTH_LOCAL}  label="LOCAL"  detail="you (client predicts)" />
      <LegendRow color={NETOBS_COLORS.AUTH_REMOTE} label="REMOTE" detail="other human (server-driven)" />
      <LegendRow color={NETOBS_COLORS.AUTH_BOT}    label="BOT"    detail="server AI (sid bot_*)" />
      <LegendRow color={NETOBS_COLORS.AUTH_NPC}    label="NPC"    detail="mob (server state-machine)" />
    </div>
  );
};
